import { Injectable } from '@angular/core';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ChatThreadDetail, ChatThreadPage } from 'src/app/models/chat';

export const THREAD_PAGE_SIZE = 50;

@Injectable({
    providedIn: 'root'
})
export class AIService {
    private env = environment;

    constructor(private http: HttpClient) { }

    /**
     * Ask a question in a conversation. The thread is one of the user's, or an id made up
     * here for a conversation the question starts.
     */
    getResponse(prompt: string, thread: string): Observable<string> {
        return this.http.post(this.env.api_root.concat('ai_api/langchain_stream/'), { prompt, thread_id: thread }, {
            responseType: 'text',
            observe: 'events',
            reportProgress: true
        }).pipe(
            // The answer so far as it streams in, then the whole of it when the
            // response ends: browsers fire progress events only now and then, so
            // the last chunk (the link definitions that turn citations into links)
            // can arrive with the final response and no progress event of its own.
            filter(event => event.type === HttpEventType.DownloadProgress || event.type === HttpEventType.Response),
            map(event => event.type === HttpEventType.Response ? event['body'] : event['partialText'])
        );

    }

    /** Questions to suggest to the user starting a chat: a few, picked afresh each time they are asked for. */
    getRecommendations(): Observable<string[]> {
        return this.http.get<{ questions: string[] }>(this.env.api_root.concat('ai_api/recommendations/'))
            .pipe(map(response => response.questions));
    }

    /**
     * The user's conversations, the one last added to first: the first page of them, or the
     * page that follows on from the cursor an earlier page gave.
     */
    listThreads(cursor?: string): Observable<ChatThreadPage> {
        const params: { [name: string]: string } = { pageSize: String(THREAD_PAGE_SIZE) };
        if (cursor) {
            params.cursor = cursor;
        }
        return this.http.get<ChatThreadPage>(this.threadsUrl(), { params });
    }

    getThread(thread: string): Observable<ChatThreadDetail> {
        return this.http.get<ChatThreadDetail>(this.threadsUrl(thread));
    }

    renameThread(thread: string, title: string): Observable<{ thread_id: string, title: string }> {
        return this.http.patch<{ thread_id: string, title: string }>(this.threadsUrl(thread), { title });
    }

    deleteThread(thread: string): Observable<void> {
        return this.http.delete<void>(this.threadsUrl(thread));
    }

    private threadsUrl(thread?: string): string {
        const url = this.env.api_root.concat('ai_api/threads/');
        return thread ? url.concat(encodeURIComponent(thread), '/') : url;
    }
}
